import type { AuthUser } from "@apple-bee/contracts";
import { KeyRound, LockKeyhole, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, errorMessage } from "../api";
import { Message } from "./Feedback";

type LoginResult =
  | {
      status: "MFA_REQUIRED";
      challengeToken: string;
    }
  | {
      status: "MFA_SETUP_REQUIRED";
      challengeToken: string;
      secret: string;
      otpauthUri: string;
    };

type MfaResult = {
  status: "AUTHENTICATED";
  user: AuthUser;
  recoveryCodes?: string[];
};

export function AuthScreen({
  invitationToken,
  recoveryToken,
  onAuthenticated,
  onInvitationCompleted,
  onRecoveryCompleted,
}: {
  invitationToken: string | null;
  recoveryToken: string | null;
  onAuthenticated: () => Promise<void>;
  onInvitationCompleted: () => void;
  onRecoveryCompleted: () => void;
}) {
  if (recoveryToken) {
    return (
      <PasswordReset
        token={recoveryToken}
        onCompleted={onRecoveryCompleted}
      />
    );
  }
  if (invitationToken) {
    return (
      <InviteAcceptance
        token={invitationToken}
        onCompleted={onInvitationCompleted}
      />
    );
  }
  return <Login onAuthenticated={onAuthenticated} />;
}

function PasswordReset({
  token,
  onCompleted,
}: {
  token: string;
  onCompleted: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirmation) {
      setError("Пароли не совпадают");
      return;
    }
    setBusy(true);
    try {
      await api("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      setPassword("");
      setConfirmation("");
      setCompleted(true);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <BrandPanel />
      <section className="auth-form-panel">
        <div className="auth-form-wrap">
          <p className="eyebrow">Восстановление доступа</p>
          {completed ? (
            <>
              <h2>Пароль изменён</h2>
              <p className="auth-intro">
                Все прежние сессии закрыты. Войдите с новым паролем и своей
                двухэтапной проверкой. Если аккаунт был временно отключён,
                IT-администратор должен отдельно включить его.
              </p>
              <Message tone="success">Ссылка больше не действует.</Message>
              <button
                className="button button--primary button--wide"
                type="button"
                onClick={onCompleted}
              >
                Перейти ко входу
              </button>
            </>
          ) : (
            <>
              <h2>Создайте новый пароль</h2>
              <p className="auth-intro">
                Ссылка одноразовая. Пароль должен содержать не менее 12
                символов, буквы и цифры.
              </p>
              <form onSubmit={(event) => void submit(event)} className="form-stack">
                <label>
                  Новый пароль
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    minLength={12}
                    required
                  />
                </label>
                <label>
                  Повторите пароль
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    minLength={12}
                    required
                  />
                </label>
                {error ? <Message tone="error">{error}</Message> : null}
                <button
                  className="button button--primary button--wide"
                  type="submit"
                  disabled={busy}
                >
                  {busy ? "Сохраняем…" : "Изменить пароль"}
                </button>
              </form>
              <button
                className="text-button text-button--center"
                type="button"
                onClick={onCompleted}
              >
                Вернуться ко входу
              </button>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function BrandPanel() {
  return (
    <aside className="auth-brand">
      <div className="brand-lockup brand-lockup--light">
        <span className="brand-mark">AB</span>
        <span>
          <strong>Apple Bee</strong>
          <small>Операционный центр</small>
        </span>
      </div>
      <div className="auth-brand__copy">
        <p className="eyebrow eyebrow--light">Закрытая система</p>
        <h1>От урожая до принятой отгрузки — в одном контролируемом контуре.</h1>
        <p>
          Складские движения, согласования и бухгалтерские выгрузки с понятной
          историей каждого действия.
        </p>
      </div>
      <div className="auth-trust">
        <span>
          <ShieldCheck size={18} /> Двухэтапный вход
        </span>
        <span>
          <LockKeyhole size={18} /> Аккаунты по приглашению
        </span>
      </div>
    </aside>
  );
}

function Login({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<LoginResult | null>(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api<LoginResult>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      setChallenge(result);
      setPassword("");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const result = useRecovery
        ? await api<MfaResult>("/api/auth/mfa/recovery", {
            method: "POST",
            body: JSON.stringify({
              challengeToken: challenge.challengeToken,
              recoveryCode,
            }),
          })
        : await api<MfaResult>("/api/auth/mfa/complete", {
            method: "POST",
            body: JSON.stringify({
              challengeToken: challenge.challengeToken,
              code,
            }),
          });
      if (result.recoveryCodes?.length) {
        setRecoveryCodes(result.recoveryCodes);
      } else {
        await onAuthenticated();
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function finishAfterRecoveryCodes() {
    await onAuthenticated();
  }

  return (
    <main className="auth-shell">
      <BrandPanel />
      <section className="auth-form-panel">
        <div className="auth-form-wrap">
          {recoveryCodes ? (
            <>
              <p className="eyebrow">Первичная настройка</p>
              <h2>Сохраните резервные коды</h2>
              <p className="auth-intro">
                Каждый код работает один раз. Храните их отдельно от телефона и
                не отправляйте в общие чаты.
              </p>
              <div className="recovery-grid">
                {recoveryCodes.map((item) => (
                  <code key={item}>{item}</code>
                ))}
              </div>
              <button
                className="button button--primary button--wide"
                type="button"
                onClick={() =>
                  void navigator.clipboard.writeText(recoveryCodes.join("\n"))
                }
              >
                Копировать коды
              </button>
              <button
                className="button button--secondary button--wide"
                type="button"
                onClick={() => void finishAfterRecoveryCodes()}
              >
                Я сохранил(а) коды
              </button>
            </>
          ) : challenge ? (
            <>
              <button className="text-button" type="button" onClick={() => setChallenge(null)}>
                ← Вернуться к email
              </button>
              <p className="eyebrow">Шаг 2 из 2</p>
              <h2>
                {challenge.status === "MFA_SETUP_REQUIRED"
                  ? "Подключите приложение-аутентификатор"
                  : "Подтвердите вход"}
              </h2>
              {challenge.status === "MFA_SETUP_REQUIRED" ? (
                <div className="totp-setup">
                  <div className="qr-wrap">
                    <QRCodeSVG value={challenge.otpauthUri} size={154} level="M" />
                  </div>
                  <p>
                    Отсканируйте QR-код в Google Authenticator, Microsoft
                    Authenticator или другом TOTP-приложении.
                  </p>
                  <details>
                    <summary>Ввести ключ вручную</summary>
                    <code className="secret-code">{challenge.secret}</code>
                  </details>
                </div>
              ) : (
                <p className="auth-intro">
                  Введите код из приложения-аутентификатора. Код обновляется
                  каждые 30 секунд.
                </p>
              )}
              <form onSubmit={(event) => void submitMfa(event)} className="form-stack">
                {useRecovery ? (
                  <label>
                    Резервный код
                    <input
                      autoFocus
                      autoComplete="one-time-code"
                      value={recoveryCode}
                      onChange={(event) => setRecoveryCode(event.target.value)}
                      placeholder="XXXXX-XXXXX"
                      required
                    />
                  </label>
                ) : (
                  <label>
                    Шестизначный код
                    <input
                      className="otp-input"
                      autoFocus
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={code}
                      onChange={(event) =>
                        setCode(event.target.value.replace(/\D/gu, ""))
                      }
                      placeholder="000 000"
                      required
                    />
                  </label>
                )}
                {error ? <Message tone="error">{error}</Message> : null}
                <button
                  className="button button--primary button--wide"
                  type="submit"
                  disabled={busy}
                >
                  {busy ? "Проверяем…" : "Продолжить"}
                </button>
              </form>
              {challenge.status === "MFA_REQUIRED" ? (
                <button
                  className="text-button text-button--center"
                  type="button"
                  onClick={() => {
                    setUseRecovery((value) => !value);
                    setError(null);
                  }}
                >
                  {useRecovery
                    ? "Использовать код из приложения"
                    : "Использовать резервный код"}
                </button>
              ) : null}
            </>
          ) : (
            <>
              <p className="eyebrow">Добро пожаловать</p>
              <h2>Вход в Apple Bee</h2>
              <p className="auth-intro">
                Используйте корпоративный аккаунт, созданный
                IT-администратором.
              </p>
              <form onSubmit={(event) => void submitPassword(event)} className="form-stack">
                <label>
                  Email
                  <input
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="name@company.kz"
                    required
                  />
                </label>
                <label>
                  Пароль
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Не менее 12 символов"
                    required
                  />
                </label>
                {error ? <Message tone="error">{error}</Message> : null}
                <button
                  className="button button--primary button--wide"
                  type="submit"
                  disabled={busy}
                >
                  <KeyRound size={18} />
                  {busy ? "Проверяем…" : "Войти"}
                </button>
              </form>
              <p className="security-note">
                Открытой регистрации нет. Для восстановления доступа обратитесь
                к IT-администратору.
              </p>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function InviteAcceptance({
  token,
  onCompleted,
}: {
  token: string;
  onCompleted: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirmation) {
      setError("Пароли не совпадают");
      return;
    }
    setBusy(true);
    try {
      await api("/api/auth/accept-invitation", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      setCompleted(true);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <BrandPanel />
      <section className="auth-form-panel">
        <div className="auth-form-wrap">
          <p className="eyebrow">Приглашение</p>
          {completed ? (
            <>
              <h2>Аккаунт создан</h2>
              <p className="auth-intro">
                Теперь войдите с новым паролем и подключите приложение для
                двухэтапной проверки.
              </p>
              <Message tone="success">Ссылка приглашения больше не действует.</Message>
              <button
                className="button button--primary button--wide"
                type="button"
                onClick={onCompleted}
              >
                Перейти ко входу
              </button>
            </>
          ) : (
            <>
              <h2>Создайте пароль</h2>
              <p className="auth-intro">
                Пароль должен содержать не менее 12 символов, буквы и цифры.
              </p>
              <form onSubmit={(event) => void submit(event)} className="form-stack">
                <label>
                  Новый пароль
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    minLength={12}
                    required
                  />
                </label>
                <label>
                  Повторите пароль
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    minLength={12}
                    required
                  />
                </label>
                {error ? <Message tone="error">{error}</Message> : null}
                <button
                  className="button button--primary button--wide"
                  type="submit"
                  disabled={busy}
                >
                  {busy ? "Создаём…" : "Создать аккаунт"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
